import { Link } from "react-router";
import { assignPlanProgress, unservedStudents, type AssignPlan } from "@edu/shared";
import { UNSENT_ROW_TEXT } from "./assign-errors.ts";

/**
 * Результат выдачи, в том числе частичный (5.7 docs/homework-phase2-design.md).
 *
 * ⚠️ «Создано 9 из 12» бесполезно: непонятно, кому дозадавать. Результат обязан
 * быть списком имён с исходом на каждое, и разметка (`batch__result`,
 * `batch__failed`) пришла сюда от пачки уроков нескольким ученикам, где это уже
 * было сделано и проверено. Самой пачки с 17.09.2026 нет: она заводила урок без
 * даты и ушла вместе с ним, а правила остались в `styles.css` ради этого
 * экрана.
 *
 * ⚠️ После нажатия форма ОСТАЁТСЯ на месте и не уходит на карточку созданной
 * работы, как делала фаза 1. При двенадцати созданных работах уходить некуда:
 * карточек двенадцать, а разное поведение для одного и для многих означало бы,
 * что человек не знает заранее, куда его унесёт. Цена названа: на пути «один
 * ученик» добавилось одно нажатие, чтобы открыть работу, и она невелика, потому
 * что смотреть там сразу после создания нечего.
 */

export interface AssignOutcomeView {
  homeworkId?: string;
  /** Хвост строки после имени: причина отказа или «не отправлено». */
  reason?: string;
}

export function AssignResult(props: {
  plan: AssignPlan;
  /** Имена берутся из списка учеников: в плане лежат только инициалы. */
  names: ReadonlyMap<string, string>;
  outcomes: ReadonlyMap<string, AssignOutcomeView>;
  busy: boolean;
  /** Ссылка «открыть список работ» ведёт в тот же разрез, из которого пришли. */
  listHref: string;
  onRetry: () => void;
}) {
  const { created, total } = assignPlanProgress(props.plan);
  const rest = unservedStudents(props.plan);

  return (
    <section className="card batch__result">
      <p className={created === 0 ? "err" : "muted"}>
        {created === 0
          ? "Домашка не задана никому"
          : `Задано ${created} из ${total}`}
      </p>

      <ul className="batch__failed">
        {props.plan.students.map((s) => {
          const name = props.names.get(s.userId) ?? s.initials;
          const outcome = props.outcomes.get(s.userId);
          if (s.status === "created") {
            const id = outcome?.homeworkId;
            return (
              <li key={s.userId}>
                {/* Каждая созданная строка это ссылка на работу: «открыть и
                    проверить» не должно требовать похода в список */}
                {id === undefined ? (
                  <strong>{name}</strong>
                ) : (
                  <Link className="textlink" to={`/homework/${id}`}>
                    {name}
                  </Link>
                )}
                : задано
              </li>
            );
          }
          return (
            <li key={s.userId}>
              <strong>{name}</strong>: {outcome?.reason ?? UNSENT_ROW_TEXT}
            </li>
          );
        })}
      </ul>

      <div className="acts">
        {rest.length > 0 && (
          /*
            ⚠️ Повтор шлёт ТЕ ЖЕ ключи идемпотентности: иначе повтор после «связь
            пропала» (а сервер работу создал) даёт вторую работу, то есть ровно
            то, ради чего ключи заведены
          */
          <button type="button" className="btn" disabled={props.busy} onClick={props.onRetry}>
            Повторить для оставшихся ({rest.length})
          </button>
        )}
        <Link className="textlink" to={props.listHref}>
          К списку домашек
        </Link>
      </div>
    </section>
  );
}
